<script setup lang="ts">
import type { Props } from './inputProps';
import SvgIcon from '@/components/icons/SvgIcon.vue';
import VaContentLoader from '@/components/framework/va-content-loader.vue';
import { useTimeout } from '@/composables/useTimeout';

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
});

const { startTimeout } = useTimeout(props.delay);

const emit = defineEmits([
  'update:modelValue',
  'keyup',
  'keydown',
  'focus',
  'blur',
]);

const onInput = (value: string) => {
  if (props.delay) {
    startTimeout(() => {
      emit('update:modelValue', value);
    });
  } else {
    emit('update:modelValue', value);
  }
};
</script>

<template>
  <div :class="['va-input', { disabled: disabled }]">
    <label>
      <span v-if="props.label" class="va-input--label">{{ props.label }}</span>
      <div class="va-input--field">
        <VaContentLoader
          v-if="props.loading"
          :style="{ width: props.width, height: '2.5rem' }"
        />
        <input
          v-else
          :class="[
            'va-input--input',
            {
              'right-align': props.rightAlign,
              'center-align': props.centerAlign,
              'has-errors': Array.isArray(props.errors)
                ? props.errors.length
                : props.errors,
              'padding-right': $slots['append-inner'] || props.clearable,
              'padding-left': $slots['prepend-inner'],
              rounded: props.rounded,
            },
          ]"
          data-testid="va-input"
          :style="{ width: props.width }"
          :type="props.type"
          :value="props.modelValue"
          :placeholder="props.placeholder"
          :min="props.min"
          :max="props.max"
          :disabled="props.disabled"
          :autocomplete="props.autocomplete"
          :maxlength="props.maxLength"
          :step="props.step"
          @focus="emit('focus', $event)"
          @blur="emit('blur')"
          @keyup="emit('keyup', $event)"
          @keydown="emit('keydown', $event)"
          @input="onInput(($event.target as HTMLInputElement).value)"
        />
        <div
          v-if="$slots['prepend-inner']"
          class="prepend-inner"
          :class="{ 'extra-padding-left': props.rounded }"
        >
          <slot name="prepend-inner"></slot>
        </div>
        <SvgIcon
          v-if="props.clearable && props.modelValue"
          icon="close"
          role="button"
          class="append-inner clickable hover-effect-highlight close-icon"
          :class="{ 'extra-padding-right': props.rounded }"
          data-testid="va-input--clear"
          @click="emit('update:modelValue', '')"
        />
        <div
          v-else-if="$slots['append-inner']"
          class="append-inner"
          :class="{ 'extra-padding-right': props.rounded }"
        >
          <slot name="append-inner"></slot>
        </div>
        <slot name="append"></slot>
      </div>
    </label>
    <div
      v-if="Array.isArray(props.errors) && props.errors.length"
      class="error-messages"
    >
      <p
        v-for="(error, index) in props.errors"
        :key="index"
        data-testid="va-input--error"
        class="error-message"
      >
        <SvgIcon icon="Warning" />
        &nbsp;
        {{ error }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.va-input {
  position: relative;
  width: 100%;

  &--field {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .append-inner {
      display: flex;
      align-items: center;
      position: absolute;
      right: 0.8rem;
      line-height: normal;

      &.extra-padding-right {
        right: 1.1rem;
      }

      &.close-icon {
        font-size: 0.7rem;
      }
    }

    .prepend-inner {
      display: flex;
      align-items: center;
      position: absolute;
      left: 0.8rem;

      &.extra-padding-left {
        left: 1.1rem;
      }
    }
  }

  &--label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    color: $color-text;
    line-height: 150%;
    text-align: left;
    cursor: pointer;
  }

  &--input {
    box-sizing: border-box;
    background-color: $color-input;
    border: 1px solid $color-input-border;
    border-radius: 0.25rem;
    outline: none;
    color: $color-text;
    height: 2.5rem;
    width: 100%;
    padding: 0 0.75rem;
    font-size: 0.875rem;

    &:hover {
      background-color: $color-input-hover;
      border-color: $color-input-border-hover;
    }

    &:focus {
      background-color: $color-input-active;
      border-color: $color-input-border-active;
    }

    &.has-errors {
      border: 1px solid $color-border-danger;
    }

    &.padding-right {
      padding-right: 1.4rem;
    }

    &.padding-left {
      padding-left: 2.2rem;
    }

    &.rounded {
      border-radius: 19.5px;
      padding-left: 1.25rem;

      &.padding-right {
        padding-right: 2.4rem;
      }

      &.padding-left {
        padding-left: 2.6rem;
      }
    }
  }

  .right-align {
    text-align: right;
    padding-right: 0;

    &.padding-right {
      padding-right: 1rem;
    }
  }

  .center-align {
    text-align: center;
  }

  .error-messages {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .error-message {
      color: $color-text-danger;
      font-size: 0.75rem;
      line-height: 1;
      display: flex;
      align-items: center;
      padding: 0;
    }
  }
}
</style>
